// Per-player identity token, scoped per room. This is the secret that proves
// "I am the same player" across reconnects, so a returning user is never
// mistaken for a different person using the same name.
//
// Stored in localStorage (survives tab eviction / reopening the invite link),
// keyed by room code, and cleared when the player leaves a finished game.

function makeToken(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual generation
  }
  // Fallback UUID v4 for environments without crypto.randomUUID.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const keyFor = (roomCode: string) => `maxbox:playerToken:${roomCode.toUpperCase()}`;

export function getPlayerToken(roomCode: string): string | null {
  try {
    return localStorage.getItem(keyFor(roomCode));
  } catch {
    return null;
  }
}

export function getOrCreatePlayerToken(roomCode: string): string {
  try {
    const existing = localStorage.getItem(keyFor(roomCode));
    if (existing) return existing;
    const token = makeToken();
    localStorage.setItem(keyFor(roomCode), token);
    return token;
  } catch {
    // localStorage unavailable (e.g. Safari private mode quota) — use a volatile
    // token so the session still works; it just won't survive a reload.
    return makeToken();
  }
}

export function clearPlayerToken(roomCode: string): void {
  try {
    localStorage.removeItem(keyFor(roomCode));
  } catch {
    // ignore
  }
}
